import { useState, useEffect, useRef, useCallback, memo, useMemo } from 'react';
import {
  Mic,
  Send,
  User,
  Bot,
  MessageSquare,
  Volume2,
  VolumeX,
  RefreshCw,
  Loader2,
  Sparkles,
  Square,
  Globe,
  Play,
  Pause,
  Volume,
} from 'lucide-react';
import { ContentItem } from '@/types/ai-learn.types';
import { learningAssistantService } from '@/services/learning-assistant.service';
import toast from 'react-hot-toast';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  mode: 'text' | 'voice';
  suggestions?: string[];
  contextInfo?: {
    course_title: string;
    current_position: string;
  };
  audioUrl?: string;
  isTranscription?: boolean;
}

interface AudioPlayerState {
  playingMessageId: string | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
}

interface CourseModule {
  _id?: string;
  id?: string;
  title: string;
}

interface CourseInfo {
  title: string;
}

interface AILiveChatProps {
  course: CourseInfo;
  courseId: string;
  currentModule: CourseModule | null;
  currentContent: ContentItem | null;
}

// Constants
import { languageOptionsDetailed } from '@/constants/languageOptions';

const LANGUAGE_OPTIONS = languageOptionsDetailed;

const AUDIO_CONFIG = {
  sampleRate: 44100,
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
  mimeType: 'audio/webm;codecs=opus'
} as const;

const INITIAL_AUDIO_PLAYER_STATE: AudioPlayerState = {
  playingMessageId: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  isMuted: false
};

// Helper functions for generating suggestions based on content type
const generateSuggestionsByContentType = (contentType: string | undefined): string[] => {
  const suggestions: string[] = [];

  switch (contentType) {
    case 'video_presentation':
      suggestions.push(
        "Summarize the key points from this video",
        "What are the main concepts covered?",
        "Give me a quiz on this video content"
      );
      break;
    case 'html_with_audio':
      suggestions.push(
        "What are the main takeaways from this lesson?",
        "Explain the concepts in simpler terms",
        "Create practice questions for this content"
      );
      break;
    case 'html':
      suggestions.push(
        "Help me understand this content",
        "What should I focus on in this lesson?",
        "Provide additional examples"
      );
      break;
    default:
      suggestions.push(
        "Explain this content in simple terms",
        "What are the key learning objectives?",
        "How does this relate to real-world applications?"
      );
  }

  return suggestions;
};

// Create user message helper
const createUserMessage = (content: string, mode: 'text' | 'voice' = 'text', isTranscription = false): Message => ({
  id: `msg_${Date.now()}`,
  role: 'user',
  content,
  timestamp: new Date(),
  mode,
  isTranscription
});

// Create assistant message helper
const createAssistantMessage = (
  content: string,
  mode: 'text' | 'voice' = 'text',
  audioUrl?: string,
  suggestions?: string[],
  contextInfo?: Message['contextInfo']
): Message => ({
  id: `msg_${Date.now() + 1}`,
  role: 'assistant',
  content,
  timestamp: new Date(),
  mode,
  audioUrl,
  suggestions,
  contextInfo
});

// Generate ID helper
const generateSessionId = (): string =>
  `ai_learn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Memoized Audio Player Component to prevent unnecessary re-renders
interface AudioPlayerProps {
  message: Message;
  audioPlayer: AudioPlayerState;
  onPlayAudio: (url: string, messageId: string) => void;
  onProgressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleMute: () => void;
  onVolumeChange: (volume: number) => void;
  formatTime: (seconds: number) => string;
}

const AudioPlayerComponent = memo(({
  message,
  audioPlayer,
  onPlayAudio,
  onProgressChange,
  onToggleMute,
  onVolumeChange,
  formatTime
}: AudioPlayerProps) => {
  const isPlaying = audioPlayer.playingMessageId === message.id;
  const progressPercent = isPlaying && audioPlayer.duration > 0
    ? (audioPlayer.currentTime / audioPlayer.duration) * 100
    : 0;
  const volumePercent = audioPlayer.volume * 100;

  return (
    <div className="mt-2 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-2 border border-indigo-100">
      {/* Progress bar - compact */}
      <div className="mb-2">
        <input
          type="range"
          min="0"
          max={Math.max(audioPlayer.duration, 1) || 1}
          value={isPlaying ? audioPlayer.currentTime : 0}
          onChange={onProgressChange}
          className="w-full h-1.5 rounded-full cursor-pointer appearance-none"
          style={{
            background: `linear-gradient(to right, rgb(99, 102, 241) 0%, rgb(168, 85, 247) ${progressPercent}%, rgb(229, 231, 235) ${progressPercent}%, rgb(229, 231, 235) 100%)`
          }}
        />
      </div>

      {/* Controls Row - compact */}
      <div className="flex items-center gap-2">
        {/* Play Button - smaller */}
        <button
          onClick={() => onPlayAudio(message.audioUrl!, message.id)}
          className={`flex-shrink-0 p-1.5 rounded-full transition-all ${
            isPlaying && audioPlayer.isPlaying
              ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
              : 'bg-white border border-indigo-300 text-indigo-600 hover:border-indigo-500'
          }`}
          aria-label={isPlaying && audioPlayer.isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying && audioPlayer.isPlaying ? (
            <Pause className="w-3 h-3" />
          ) : (
            <Play className="w-3 h-3" />
          )}
        </button>

        {/* Time Display - compact */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between text-[10px] font-medium text-indigo-700">
            <span>{isPlaying ? formatTime(audioPlayer.currentTime) : '0:00'}</span>
            <span className="text-gray-400">{isPlaying && audioPlayer.duration > 0 ? formatTime(audioPlayer.duration) : '0:00'}</span>
          </div>
        </div>

        {/* Volume Control - compact */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onToggleMute}
            className="p-1 rounded transition-all hover:bg-white text-indigo-600"
            title={audioPlayer.isMuted ? 'Unmute' : 'Mute'}
            aria-label={audioPlayer.isMuted ? 'Unmute' : 'Mute'}
          >
            {audioPlayer.isMuted ? (
              <VolumeX className="w-3 h-3" />
            ) : (
              <Volume className="w-3 h-3" />
            )}
          </button>

          {/* Volume Slider - compact */}
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={audioPlayer.isMuted ? 0 : audioPlayer.volume}
            onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
            className="h-1.5 rounded-full cursor-pointer appearance-none hidden sm:block"
            style={{
              width: '40px',
              background: `linear-gradient(to right, rgb(99, 102, 241) 0%, rgb(168, 85, 247) ${volumePercent}%, rgb(229, 231, 235) ${volumePercent}%, rgb(229, 231, 235) 100%)`
            }}
            title="Volume control"
          />
        </div>
      </div>
    </div>
  );
});

AudioPlayerComponent.displayName = 'AudioPlayerComponent';

// Memoized Message Component
interface MessageComponentProps {
  message: Message;
  audioPlayer: AudioPlayerState;
  onPlayAudio: (url: string, messageId: string) => void;
  onProgressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleMute: () => void;
  onVolumeChange: (volume: number) => void;
  onSendSuggestion: (suggestion: string) => void;
  formatTime: (seconds: number) => string;
  isLoading: boolean;
}

const MessageComponent = memo(({
  message,
  audioPlayer,
  onPlayAudio,
  onProgressChange,
  onToggleMute,
  onVolumeChange,
  onSendSuggestion,
  formatTime,
  isLoading
}: MessageComponentProps) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-2 animate-fadeIn`}>
      <div
        className={`max-w-[90%] sm:max-w-[80%] rounded-xl shadow-sm ${
          isUser
            ? 'bg-gradient-to-br from-indigo-600 to-indigo-700 text-white'
            : 'bg-white text-gray-800 border border-gray-100'
        }`}
      >
        <div className="flex items-start gap-2 p-2.5">
          {!isUser && (
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Bot className="w-3 h-3 text-white" />
            </div>
          )}
          {isUser && (
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
              <User className="w-3 h-3" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            {/* Voice transcription badge - compact */}
            {message.isTranscription && (
              <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium mb-1.5 ${
                isUser
                  ? 'bg-white/20 text-white/90'
                  : 'bg-indigo-50 text-indigo-600'
              }`}>
                <Mic className="w-2.5 h-2.5" />
                Voice
              </div>
            )}

            {/* Message content - smaller text */}
            <div className="text-xs leading-relaxed break-words whitespace-pre-wrap">
              {message.content}
            </div>

            {/* Audio player */}
            {message.audioUrl && (
              <AudioPlayerComponent
                message={message}
                audioPlayer={audioPlayer}
                onPlayAudio={onPlayAudio}
                onProgressChange={onProgressChange}
                onToggleMute={onToggleMute}
                onVolumeChange={onVolumeChange}
                formatTime={formatTime}
              />
            )}

            {/* Suggestions - more compact */}
            {message.suggestions && message.suggestions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {message.suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => onSendSuggestion(suggestion)}
                    disabled={isLoading}
                    className="group px-2 py-1 text-[10px] bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg hover:from-indigo-100 hover:to-purple-100 hover:border-indigo-300 transition-all duration-200 text-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Sparkles className="w-2.5 h-2.5" />
                    <span>{suggestion}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Timestamp - smaller */}
            <div className={`text-[10px] mt-1.5 ${
              isUser ? 'text-white/60' : 'text-gray-400'
            }`}>
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

MessageComponent.displayName = 'MessageComponent';

export default function AILiveChat({
  course,
  courseId,
  currentModule,
  currentContent
}: AILiveChatProps) {
  // State management
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [interactionMode, setInteractionMode] = useState<'text' | 'voice'>('text');
  const [conversationId, setConversationId] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [showSettings, setShowSettings] = useState(false);

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  // Consolidated audio player state
  const [audioPlayer, setAudioPlayer] = useState<AudioPlayerState>(INITIAL_AUDIO_PLAYER_STATE);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement>(null);

  // Initialize conversation
  useEffect(() => {
    const sessionId = generateSessionId();
    setConversationId(sessionId);

    // Add welcome message with context
    const welcomeMessage = createAssistantMessage(
      `Hello! I'm your AI learning assistant. I can help you with your current course: "${course?.title}". ${currentModule ? `You're currently in module: "${currentModule.title}".` : ''} ${currentContent ? `Looking at: "${currentContent.title}".` : ''} How can I assist you?`,
      'text',
      undefined,
      generateContextualSuggestions(),
      {
        course_title: course?.title || '',
        current_position: `${currentModule?.title || 'Module'} - ${currentContent?.title || 'Content'}`
      }
    );

    setMessages([welcomeMessage]);
  }, [course, currentModule, currentContent]);

  // Generate suggestions based on context
  const generateContextualSuggestions = useCallback((): string[] => {
    if (currentContent) {
      return generateSuggestionsByContentType(currentContent.type).slice(0, 3);
    } else if (currentModule) {
      return [
        `What will I learn in ${currentModule.title}?`,
        "Show me the learning path for this module",
        "What are the prerequisites for this module?"
      ];
    }
    return [
      `Tell me about the ${course?.title} course`,
      "What are the main topics covered?",
      "How should I approach this course?"
    ];
  }, [currentContent, currentModule, course]);

  // Auto-scroll to bottom - wrapped in useCallback for optimization
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Close settings when clicking outside
  useEffect(() => {
    if (!showSettings) return;

    const handleClickOutside = () => setShowSettings(false);
    document.addEventListener('click', handleClickOutside);

    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSettings]);

  // Setup audio player event listeners with throttling for better performance
  useEffect(() => {
    const audio = audioPlayerRef.current;
    if (!audio) return;

    let lastUpdateTime = 0;
    const UPDATE_THROTTLE_MS = 100; // Throttle updates to 10 FPS instead of 60 FPS

    const handleTimeUpdate = () => {
      const now = Date.now();
      // Throttle state updates to prevent excessive re-renders
      if (now - lastUpdateTime < UPDATE_THROTTLE_MS) return;
      lastUpdateTime = now;

      setAudioPlayer(prev => ({
        ...prev,
        currentTime: audio.currentTime,
        // Capture duration during timeupdate if it's still 0
        ...(prev.duration === 0 && audio.duration && isFinite(audio.duration)
          ? { duration: audio.duration }
          : {})
      }));
    };

    const handleLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setAudioPlayer(prev => ({
          ...prev,
          duration: audio.duration
        }));
      }
    };

    const handleDurationChange = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setAudioPlayer(prev => ({
          ...prev,
          duration: audio.duration
        }));
      }
    };

    const handleCanPlay = () => {
      // Capture duration as soon as audio can play
      if (audio.duration && isFinite(audio.duration)) {
        setAudioPlayer(prev => ({
          ...prev,
          duration: audio.duration
        }));
      }
    };

    const handlePlay = () => {
      setAudioPlayer(prev => ({ ...prev, isPlaying: true }));
    };

    const handlePause = () => {
      setAudioPlayer(prev => ({ ...prev, isPlaying: false }));
    };

    const handleEnded = () => {
      setAudioPlayer(prev => ({
        ...prev,
        isPlaying: false,
        currentTime: 0,
        playingMessageId: null
      }));
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONFIG
      });

      streamRef.current = stream;

      // Set up audio level monitoring
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      analyserRef.current.fftSize = 256;
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateAudioLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
          setAudioLevel(average / 255 * 100);
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        }
      };
      updateAudioLevel();

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: AUDIO_CONFIG.mimeType
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
        sendAudioMessage(audioBlob);

        // Cleanup
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        setAudioLevel(0);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Recording started...');

    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      toast.success('Recording stopped');
    }
  };

  // Send audio message
  const sendAudioMessage = async (audioBlob: Blob) => {
    if (!conversationId) return;

    setIsLoading(true);

    try {
      // Create form data
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.webm');

      // Send audio with context
      const response = await learningAssistantService.sendAudioMessage({
        audio_file: audioBlob,
        session_id: conversationId,
        course_id: courseId,
        module_id: currentModule?._id || currentModule?.id || '',
        scene_id: currentContent?._id || currentContent?.id || '',
        return_audio: interactionMode === 'voice',
        language: selectedLanguage
      });

      if (response.user_transcript || response.transcribed_text) {
        const userMessage = createUserMessage(
          response.user_transcript || response.transcribed_text || '',
          'voice',
          true
        );
        setMessages(prev => [...prev, userMessage]);
      }

      if (response.response_text) {
        const assistantMessage = createAssistantMessage(
          response.response_text,
          interactionMode,
          response.audio_url,
          response.suggestions || generateContextualSuggestions()
        );
        setMessages(prev => [...prev, assistantMessage]);

        // Play audio response if available - sync with player state
        if (response.audio_url) {
          playAudio(response.audio_url, assistantMessage.id);
        }
      }
    } catch (error) {
      console.error('Error sending audio message:', error);
      toast.error('Failed to send audio message');
    } finally {
      setIsLoading(false);
    }
  };

  // Send text message
  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputMessage;
    if (!textToSend.trim() || !conversationId) return;

    const userMessage = createUserMessage(textToSend, 'text');
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Send message with full context
      const response = await learningAssistantService.sendTextMessage({
        message: textToSend,
        session_id: conversationId,
        course_id: courseId,
        module_id: currentModule?._id || currentModule?.id || '',
        scene_id: currentContent?._id || currentContent?.id || '',
        include_audio_response: interactionMode === 'voice',
        language: selectedLanguage
      });

      const assistantMessage = createAssistantMessage(
        response.response_text,
        interactionMode,
        response.audio_url,
        response.suggestions || generateContextualSuggestions(),
        {
          course_title: course?.title || '',
          current_position: `${currentModule?.title || 'Module'} - ${currentContent?.title || 'Content'}`
        }
      );

      setMessages(prev => [...prev, assistantMessage]);

      // Play audio response if available - sync with player state
      if (response.audio_url && interactionMode === 'voice') {
        playAudio(response.audio_url, assistantMessage.id);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  // Switch interaction mode
  const switchMode = useCallback((mode: 'text' | 'voice') => {
    setInteractionMode(mode);
  }, []);

  // Clear conversation
  const clearConversation = useCallback(() => {
    setMessages([]);
    const sessionId = generateSessionId();
    setConversationId(sessionId);
    toast.success('Conversation cleared');
  }, []);

  // Audio player helper functions
  const formatTime = useCallback((seconds: number): string => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }, []);

  const playAudio = useCallback((audioUrl: string, messageId: string) => {
    if (audioPlayerRef.current && audioPlayer.playingMessageId !== messageId) {
      audioPlayerRef.current.src = audioUrl;
      audioPlayerRef.current.volume = audioPlayer.isMuted ? 0 : audioPlayer.volume;
      audioPlayerRef.current.play().catch(console.error);
      // Reset duration when switching to a new audio track
      setAudioPlayer(prev => ({
        ...prev,
        playingMessageId: messageId,
        currentTime: 0,
        duration: 0
      }));
    } else if (audioPlayer.playingMessageId === messageId && audioPlayerRef.current) {
      if (audioPlayer.isPlaying) {
        audioPlayerRef.current.pause();
      } else {
        audioPlayerRef.current.play().catch(console.error);
      }
    }
  }, [audioPlayer.playingMessageId, audioPlayer.isMuted, audioPlayer.volume, audioPlayer.isPlaying]);

  const handleProgressChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.currentTime = newTime;
      setAudioPlayer(prev => ({ ...prev, currentTime: newTime }));
    }
  }, []);

  const toggleMute = useCallback(() => {
    setAudioPlayer(prev => {
      const newMuted = !prev.isMuted;
      if (audioPlayerRef.current) {
        audioPlayerRef.current.volume = newMuted ? 0 : prev.volume;
      }
      return { ...prev, isMuted: newMuted };
    });
  }, []);

  const handleVolumeChange = useCallback((newVolume: number) => {
    setAudioPlayer(prev => {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.volume = newVolume;
      }
      return {
        ...prev,
        volume: newVolume,
        isMuted: newVolume === 0 ? prev.isMuted : false
      };
    });
  }, []);

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Compact Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-2.5 text-white shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Bot className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-xs font-bold">AI Assistant</h3>
              <p className="text-[10px] text-white/70">Here to help</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSettings(!showSettings);
                }}
                className="p-1.5 hover:bg-white/20 rounded-md transition-all text-xs flex items-center gap-1"
                title="Language"
              >
                <Globe className="w-3 h-3" />
              </button>

              {showSettings && (
                <div
                  className="absolute right-0 top-9 bg-white text-gray-800 rounded-lg shadow-xl border border-gray-200 p-2 w-48 z-50 animate-fadeIn"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-[10px] font-semibold mb-2 text-gray-600 flex items-center gap-1.5">
                    <Globe className="w-3 h-3" />
                    Language
                  </div>
                  <div className="max-h-40 overflow-y-auto custom-scrollbar">
                    {LANGUAGE_OPTIONS.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setSelectedLanguage(lang.code);
                          setShowSettings(false);
                          toast.success(`Language: ${lang.name}`);
                        }}
                        className={`w-full text-left px-2 py-1.5 text-[10px] rounded hover:bg-indigo-50 transition-all flex items-center gap-1.5 ${
                          selectedLanguage === lang.code ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-xs">{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Mode Selection */}
            <div className="flex gap-0.5 bg-white/10 backdrop-blur-sm rounded-md p-0.5">
              <button
                onClick={() => switchMode('text')}
                className={`p-1.5 rounded text-xs transition-all ${
                  interactionMode === 'text'
                    ? 'bg-white text-indigo-600'
                    : 'text-white hover:bg-white/10'
                }`}
                title="Text"
              >
                <MessageSquare className="w-3 h-3" />
              </button>
              <button
                onClick={() => switchMode('voice')}
                className={`p-1.5 rounded text-xs transition-all ${
                  interactionMode === 'voice'
                    ? 'bg-white text-indigo-600'
                    : 'text-white hover:bg-white/10'
                }`}
                title="Voice"
              >
                <Volume2 className="w-3 h-3" />
              </button>
            </div>

            {/* Clear Chat */}
            <button
              onClick={clearConversation}
              className="p-1.5 hover:bg-white/20 rounded-md transition-all"
              title="Clear"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Context Info - Compact */}
        <div className="mt-1.5">
          <div className="text-[10px] text-white/80 flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-md px-2 py-1">
            <Sparkles className="w-2.5 h-2.5 flex-shrink-0" />
            <span className="truncate font-medium">
              {currentContent ? `${currentContent.title}` : currentModule ? `${currentModule.title}` : `${course?.title}`}
            </span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-1">
        {messages.map((message) => (
          <MessageComponent
            key={message.id}
            message={message}
            audioPlayer={audioPlayer}
            onPlayAudio={playAudio}
            onProgressChange={handleProgressChange}
            onToggleMute={toggleMute}
            onVolumeChange={handleVolumeChange}
            onSendSuggestion={sendMessage}
            formatTime={formatTime}
            isLoading={isLoading}
          />
        ))}

        {isLoading && (
          <div className="flex justify-start mb-2 animate-fadeIn">
            <div className="bg-white text-gray-800 shadow-sm border border-gray-100 rounded-xl p-2.5">
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <Bot className="w-3 h-3 text-white" />
                </div>
                <div className="flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                  <span className="text-xs font-medium text-gray-700">Thinking...</span>
                  <div className="flex gap-0.5">
                    <span className="w-1 h-1 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-1 h-1 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-1 h-1 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Compact */}
      <div className="border-t border-gray-200 bg-white p-2 sm:p-2.5">
        {interactionMode === 'voice' && (
          <div className="mb-2 flex items-center justify-center gap-2">
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isLoading}
              className={`relative p-3 rounded-full transition-all shadow-md ${
                isRecording
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white animate-pulse'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isRecording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              {isRecording && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </span>
              )}
            </button>

            {isRecording && (
              <div className="flex items-center gap-2">
                <div className="flex flex-col gap-0.5">
                  <div className="text-[10px] font-semibold text-red-600 animate-pulse">Recording...</div>
                  <div className="w-20 sm:w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-red-500 to-red-600 transition-all duration-100 rounded-full"
                      style={{ width: `${audioLevel}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-1.5">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={
              interactionMode === 'voice'
                ? "Type or use voice..."
                : "Type your message..."
            }
            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-xs placeholder:text-gray-400 bg-white"
            disabled={isLoading}
          />
          <button
            onClick={() => sendMessage()}
            disabled={isLoading || !inputMessage.trim()}
            className="px-3 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Hidden audio player element */}
      <audio ref={audioPlayerRef} className="hidden" />
    </div>
  );
}